import { ReactNode } from "react";

import { AddressPrediction } from "@smart/bridge-resources-basic";
import {
  DurationUnit,
  FieldType,
  GroupType,
  LinkConditionType,
  MeetingType,
  NullableSmokeballAddress,
  ResponseSyncStatus,
  SubmissionCommunicationMethod,
  SubmissionStatus,
  SubmissionSyncStatus,
  TimeOfDay,
} from "@smart/bridge-types-basic";
import { UploadFn } from "@smart/itops-sb-design-system-dom";
import { TimeZone } from "@smart/itops-utils-basic";

export const sectionFallback = "Section";
export const summaryTitle = "Summary";
export const printLinkText = "Click here to save a copy of your form";
export const printTitle = "Intake Form";

export type FieldComponentType =
  | FieldType
  | "mapped"
  | "group"
  | "contactDetails";

export const fieldComponentNames: Record<FieldComponentType, string> = {
  mapped: "Mapped fields",
  group: "Group of fields",
  info: "Text",
  checkbox: "Multiple choice",
  choice: "Single select",
  yesNo: "Yes and No",
  text: "Short answer",
  multilineText: "Long answer",
  number: "Number",
  date: "Date",
  currency: "Currency",
  phoneNumber: "Phone number",
  address: "Address",
  email: "Email",
  payment: "Smokeball Payments",
  appointment: "Appointment",
  file: "File upload",
  signature: "Signature",
  contactDetails: "Contact details",
};

export const fieldFallbackLabel: Record<
  FieldComponentType | "section",
  string
> = {
  ...fieldComponentNames,
  section: sectionFallback,
};

export type TeamItem = {
  name: string;
  picture?: string | null;
  email?: string | null;
  phone?: string;
  uri?: string;
  source?: string;
};

export type ConnectionStatus = "initial" | "loading" | "error" | "success";

export type ConnectionItem = {
  status: ConnectionStatus;
  error?: string;
};

export type LinkItem = {
  condition: LinkConditionType;
  fieldUri: string;
  value: string;
  hide?: boolean | null;
};

export type SectionItem = {
  uri: string;
  title?: string | null;
  description?: string | null;
  hasRequired?: boolean;
  links?: LinkItem[] | null;
  validateOnDisplay?: boolean;
  translations?:
    | {
        language: string;
        title?: string | null;
        description?: string | null;
      }[]
    | null;
};

export type FieldError = {
  name: string;
  label?: string | null;
  index?: number;
  error?: string;
  fieldErrors?: undefined;
};

export type GroupError = {
  name: string;
  label?: string | null;
  index?: number;
  error?: string;
  fieldErrors: FieldError[];
};

export type SectionErrors = {
  total: number;
  errors: (FieldError | GroupError)[];
};

export type FieldItem<T extends FieldType = FieldType> = {
  uri: string;
  sectionUri: string;
  groupUri?: string | null;
  order: string;
  label?: string | null;
  description?: string | null;
  hint?: string | null;
  type: T;
  options?:
    | {
        value: string;
        label: string;
      }[]
    | null;
  allowCustomResponse?: boolean | null;
  mandatory?: boolean | null;
  links?: LinkItem[] | null;
  layout?: {
    id: string;
    providerId: string;
    name: string;
    parentId?: string | null;
    parentName?: string | null;
    parentProviderId?: string | null;
    displayName?: string | null;
  } | null;
  field?: {
    name: string;
    details?: string | null;
    dataSourceMapping?: string | null;
  } | null;
  availableStaffIds?: string[] | null;
  duration?: number | null;
  durationUnit?: DurationUnit | null;
  availability?:
    | {
        day: number;
        enabled: boolean;
        fromTime: {
          hour: number;
          minute: number;
        };
        toTime: {
          hour: number;
          minute: number;
        };
      }[]
    | null;
  timezone?: string | null;
  bufferTime?: number | null;
  minimumNotice?: number | null;
  meetingType?: MeetingType | null;
  skipEventInvite?: boolean | null;
  payment?: {
    amountInCents?: number | null;
    bankAccount?: {
      id: string;
      type: number;
    } | null;
  } | null;
  signature?: {
    method?: "electronic" | "digital" | null;
  } | null;
  hasDynamicOptions?: boolean | null;
  includeInSummary?: boolean | null;
  translations?:
    | {
        language: string;
        label?: string | null;
        description?: string | null;
        options?:
          | {
              value: string;
              label: string;
            }[]
          | null;
      }[]
    | null;
};

// Support booking for a single staff/team member only.
export type SelectedAppointment = {
  selectedDate?: string | null;
  selectedTeamMember?: {
    id: string;
    firstName?: string | null | undefined;
    middleName?: string | null | undefined;
    lastName: string;
  } | null;
  selectedTime?: TimeOfDay | null;
  timezone?: TimeZone;
};

export type GroupItem = {
  uri: string;
  sectionUri: string;
  order: string;
  description?: string | null;
  label?: string | null;
  hint?: string | null;
  repeatable?: boolean | null;
  minRepeat?: number | null;
  maxRepeat?: number | null;
  repeatPrompt?: string | null;
  links?: LinkItem[] | null;
  type: GroupType;
  translations?:
    | {
        language: string;
        label?: string | null;
        description?: string | null;
        repeatPrompt?: string | null;
      }[]
    | null;
};

export type SubmissionAutoFillStatus =
  | "completed"
  | "generated"
  | "generating"
  | "reviewed"
  | "failed";

export type SubmissionItem = {
  uri: string;
  deleted?: boolean | null;
  responses?: Record<
    string,
    {
      value?: any;
      syncStatus?: ResponseSyncStatus;
    }
  >;
  status?: SubmissionStatus;
  autoFillStatus?: SubmissionAutoFillStatus;
  aiFillFromMatterInfo?: boolean | null;
  syncStatus?: SubmissionSyncStatus | null;
  lastUpdatedSectionUri?: string;
  updatedAt?: string;
  communicationMethod?: SubmissionCommunicationMethod | null;
  recipient?: {
    firstName?: string | null;
    lastName?: string | null;
  } | null;
};

export type OrderedGroupFieldSet = {
  visibleFields: FieldItem[];
  index?: number;
};

export type OrderedGroup = {
  type: "group";
  order: string;
  group: GroupItem;
  fields: FieldItem[];
  groupFieldSets: OrderedGroupFieldSet[];
};

export type OrderedField = { type: "field"; order: string; field: FieldItem };

export type OrderedItem = OrderedField | OrderedGroup;

export type OrderedSection = {
  section: SectionItem;
  orderedItems: OrderedItem[];
};

export type ResponseSubmitter = (props: {
  responses: Record<string, any>;
  status?: SubmissionStatus;
  lastUpdatedSectionUri?: string;
  lastUpdatedFieldUri?: string;
  hasDynamicOptions?: boolean;
}) => Promise<
  | {
      operationIds: string[];
      submissionStatus: { status?: SubmissionStatus; deleted?: boolean };
      error?: string;
      fieldsWithDynamicOptions?: FieldItem[];
      digitalSignature?: { signingUrl?: string };
    }
  | undefined
>;

export type LookupOptions = {
  addressLookup: {
    search: ({
      search,
      sessionKey,
    }: {
      search: string;
      sessionKey?: string;
    }) => {
      result?: AddressPrediction[];
      loading?: boolean;
    };
    load: (props: {
      placeId: string;
      sessionKey?: string;
    }) => Promise<NullableSmokeballAddress | null | undefined>;
  };
  fileLookup: {
    load: ({
      fieldUri,
      fileNames,
    }: {
      fieldUri: string;
      fileNames: string[];
    }) => Promise<{ key: string; uploadUrl: string }[] | undefined>;
    upload: UploadFn;
  };
};

export type SubmissionStatusItem = {
  status?: SubmissionStatus;
  deleted?: boolean;
};

export type IntakeFormSectionRef = {
  submitForm: () => void;
  errors: SectionErrors | undefined;
  connection?: ConnectionItem;
  setFieldValue?: (fieldUri: string, value: any) => void;
  hasUploadingFiles?: () => boolean;
};

export type LoadResponses = () => Promise<
  Record<string, { value?: any; updatedAt: string }> | undefined
>;

export const bufferTimes = [15, 30, 45, 60];
export const minimumNoticesInMinute = [60, 240, 480, 1440, 2880, 4320, 10080]; // [1hr, 4hr, 8hr, 1day, 2 days, 3 days, 7 days]

export enum WeekDay {
  Sunday,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
}
export const calendarWeek = [
  WeekDay.Monday,
  WeekDay.Tuesday,
  WeekDay.Wednesday,
  WeekDay.Thursday,
  WeekDay.Friday,
  WeekDay.Saturday,
  WeekDay.Sunday,
];
export const calendarWeekLabel: Record<WeekDay, string> = {
  [WeekDay.Monday]: "Monday",
  [WeekDay.Tuesday]: "Tuesday",
  [WeekDay.Wednesday]: "Wednesday",
  [WeekDay.Thursday]: "Thursday",
  [WeekDay.Friday]: "Friday",
  [WeekDay.Saturday]: "Saturday",
  [WeekDay.Sunday]: "Sunday",
};

export const calendarWeekShortLabel: Record<WeekDay, string> = {
  [WeekDay.Monday]: "Mo",
  [WeekDay.Tuesday]: "Tu",
  [WeekDay.Wednesday]: "We",
  [WeekDay.Thursday]: "Th",
  [WeekDay.Friday]: "Fr",
  [WeekDay.Saturday]: "Sa",
  [WeekDay.Sunday]: "Su",
};

export const minTimeOfDay: TimeOfDay = { hour: 6, minute: 0 };
export const maxTimeOfDay: TimeOfDay = { hour: 20, minute: 0 };
export const startWorkingTimeOfDay: TimeOfDay = { hour: 9, minute: 0 };
export const endWorkingTimeOfDay: TimeOfDay = { hour: 17, minute: 0 };

export const MAX_MINS_IN_PAST = 2;

export const calendarMonthsShort = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const calendarWeekDays = [
  WeekDay.Monday,
  WeekDay.Tuesday,
  WeekDay.Wednesday,
  WeekDay.Thursday,
  WeekDay.Friday,
  WeekDay.Saturday,
  WeekDay.Sunday,
];

export type AutoFillStatus = "filling" | "completed" | "failed";

export type ToastContent = {
  text: string;
  type: "success" | "error";
};

export type Toast = {
  text: string | ReactNode;
  palette?: "default" | "success" | "warning" | "destructive" | "branding";
  width?: "small" | "medium" | "maximum";
  actions?: {
    text: string;
    variant?: "default" | "subtle" | "inverted";
    onClick: () => void;
  }[];
};

export const betaColour = "rgb(235,46,109)";

export const APPT_DEFAULT_DURATION = 30;
export const appointmentDurationUnits = ["hours", "minutes"] as const;
export const APPT_DEFAULT_DURATION_UNIT = appointmentDurationUnits[1];

export const syncStatusColour: Record<ResponseSyncStatus, string> = {
  synced: "text-green-600",
  notSynced: "text-red-550",
  syncing: "text-yellow-450",
  failedToSync: "text-red-550",
  loading: "text-yellow-450",
  loaded: "text-green-600",
  failedToLoad: "text-red-550",
  notToSync: "text-red-550",
  pendingContactReview: "text-yellow-550",
  reviewed: "text-green-600",
};

export const syncStatusLabel: Record<ResponseSyncStatus, string> = {
  synced: "Synced",
  notSynced: "Not synced",
  syncing: "Syncing",
  failedToSync: "Failed to sync",
  loading: "Loading",
  loaded: "Loaded",
  failedToLoad: "Failed to load",
  notToSync: "Not to sync",
  pendingContactReview: "Pending contact review",
  reviewed: "Reviewed",
};
